import { downsampleBuffer, pcmEncode } from "./audioUtils";
import { EventStreamCodec, Message } from "@aws-sdk/eventstream-codec";
import { fromUtf8, toUtf8 } from "@aws-sdk/util-utf8-node";
import { MicrophoneStream } from "@/utils/MicrophoneStream";

interface AWSTranscriptResponse {
    Transcript: AWSTranscriptResponseTranscript;
}
interface AWSTranscriptResponseTranscript {
    Results: [AWSTranscriptResponseTranscriptResult];
}

interface AWSTranscriptResponseTranscriptResult {
    Alternatives: [AWSTranscriptResponseTranscriptResultAlternative];
    IsPartial: boolean;
    LanguageCode: string;
}

interface AWSTranscriptResponseTranscriptResultAlternative {
    Transcript: string;
}

export interface TranscriptionStreamResult {
    transcriptText: string;
    transcriptLanguage: string;
    isPartial: boolean;
}

export class StreamTranscriptionClient {
    transcribeStreamURLFetcher: () => Promise<string>;
    transcripts: string;
    inputSampleRate: number;
    socket: WebSocket | null;
    resultsStream: TransformStream;
    transcribeException: boolean;
    socketError: boolean;
    eventStreamMarshaller: EventStreamCodec;
    micStream: MicrophoneStream;
    streamActive: boolean;

    constructor({ transcribeStreamURLFetcher }: { transcribeStreamURLFetcher: () => Promise<string> }) {
        this.transcribeStreamURLFetcher = transcribeStreamURLFetcher;
        this.streamActive = false;
        this.transcripts = "";
        this.inputSampleRate = 8000;
        this.socket = null;
        this.resultsStream = new TransformStream();
        this.transcribeException = false;
        this.socketError = false;
        this.eventStreamMarshaller = new EventStreamCodec(toUtf8, fromUtf8);
        this.micStream = new MicrophoneStream();
    }

    async stopStream(): Promise<void> {
        // To end the data stream, send an empty audio chunk in an event stream encoded message.
        // https://docs.aws.amazon.com/transcribe/latest/dg/streaming-setting-up.html
        const binary = this.convertAudioToBinaryMessage(new Float32Array());

        if (this.socket?.readyState === this.socket?.OPEN && binary != null) {
            this.socket?.send(binary);
        }

        // Sending the above should close the stream
        let attempts = 0;
        while (this.streamActive) {
            attempts++;
            if (attempts > 50) {
                // 5 seconds
                this.socket?.close();
                throw new Error("Stream did not gracefully terminate after 5 second");
            }
            await new Promise((r) => setTimeout(r, 100));
        }
    }

    async registerStream(rawStream: MediaStream): Promise<void> {
        this.resultsStream = new TransformStream();

        const url = await this.transcribeStreamURLFetcher();

        this.socket = new WebSocket(url);
        this.socket.binaryType = "arraybuffer";

        this.socket.onopen = () => {
            this.streamActive = true;
            const results = this.micStream.listenToStream(rawStream, (chunk) => {
                const binary = this.convertAudioToBinaryMessage(chunk);

                if (this.socket?.readyState === this.socket?.OPEN && binary != null) {
                    this.socket?.send(binary);
                }
            });
            this.inputSampleRate = results.sampleRate;
        };

        const defaultWriter = this.resultsStream.writable.getWriter();
        await defaultWriter.ready;

        this.socket.onmessage = (message) => {
            const data = new Uint8Array(message.data);

            //convert the binary event stream message to JSON
            const messageWrapper = this.eventStreamMarshaller.decode(data);
            const messageBody = JSON.parse(String.fromCharCode(...messageWrapper.body));

            if (messageWrapper.headers[":message-type"].value === "event") {
                this.handleEventStreamMessage(messageBody, defaultWriter);
            } else {
                this.transcribeException = true;
                console.error("Error parsing socket message", messageBody.Message);
            }
        };

        this.socket.onerror = (e) => {
            this.streamActive = false;
            this.socketError = true;
            console.error("Error reporetd in socket", e);
        };

        this.socket.onclose = (closeEvent) => {
            this.streamActive = false;
            this.micStream?.stopListening();

            console.log("Socket closed", closeEvent.code, closeEvent.reason || "");
            // the close event immediately follows the error event; only handle one.
            if (!this.socketError && !this.transcribeException) {
                if (closeEvent.code !== 1000) {
                    console.error("Streaming Exception " + closeEvent.reason);
                }
                // toggleStartStop();
            }
        };

        // Sending the above should close the stream
        let attempts = 0;
        while (this.streamActive) {
            attempts++;
            if (attempts > 500) {
                // 10 seconds
                throw new Error("Stream did not gracefully terminate after 5 second, closing manually");
            }
            await new Promise((r) => setTimeout(r, 100));
        }
    }

    async iterator(): Promise<AsyncIterable<TranscriptionStreamResult>> {
        return streamAsyncIterator(this.resultsStream.readable);
    }

    convertAudioToBinaryMessage(chunk: Float32Array): ArrayBufferLike | null {
        const sampleRate = 8000;

        // downsample and convert the raw audio bytes to PCM
        const downsampledBuffer = downsampleBuffer(chunk, this.inputSampleRate, sampleRate);
        const pcmEncodedBuffer = pcmEncode(downsampledBuffer);

        // add the right JSON headers and structure to the message
        const audioEventMessage = this.getAudioEventMessage(new Uint8Array(pcmEncodedBuffer));

        return this.eventStreamMarshaller.encode(audioEventMessage);
    }

    getAudioEventMessage(buffer: Uint8Array): Message {
        return {
            headers: {
                ":message-type": {
                    type: "string",
                    value: "event",
                },
                ":event-type": {
                    type: "string",
                    value: "AudioEvent",
                },
            },
            body: buffer,
        };
    }

    handleEventStreamMessage(messageJson: AWSTranscriptResponse, writer: WritableStreamDefaultWriter) {
        const results = messageJson.Transcript.Results;
        if (results.length > 0) {
            if (results[0].Alternatives.length > 0) {
                let transcriptText = results[0].Alternatives[0].Transcript;
                const transcriptLanguage = results[0].LanguageCode;
                const isPartial = results[0].IsPartial;

                // fix encoding for accented characters
                transcriptText = decodeURIComponent(escape(transcriptText));

                if (!results[0].IsPartial) {
                    writer.write({ transcriptText, transcriptLanguage, isPartial } as TranscriptionStreamResult);
                }
            }
        }
    }
}

function streamAsyncIterator(stream: ReadableStream): AsyncIterable<TranscriptionStreamResult> {
    return {
        [Symbol.asyncIterator]: () => {
            const reader = stream.getReader();
            return { next: (): Promise<IteratorResult<TranscriptionStreamResult>> => reader.read() as Promise<IteratorResult<TranscriptionStreamResult>> };
        },
    };
}
